// Contact management service
import { supabase } from './supabase';
import { noderr, DatabaseError, ValidationError } from '../../shared/utils/errorHandler';
import type { 
  Contact, 
  CreateContactData, 
  UpdateContactData, 
  ContactsFilter,
  ContactStats
} from '../../shared/types/entities';

// Enhanced error codes for better error handling
export enum ContactErrorCode {
  DUPLICATE_EMAIL = 'E_DUPLICATE_EMAIL',
  RLS_DENIED = 'E_RLS_DENIED',
  VALIDATION = 'E_VALIDATION',
  NOT_FOUND = 'E_NOT_FOUND'
}

export interface ContactServiceError {
  code: ContactErrorCode;
  message: string;
  field?: string;
}

export interface BatchDeleteResult {
  success: string[];
  failed: Array<{ id: string; error: string }>;
}

export interface ContactWithActivity extends Contact {
  artists_count: number;
  campaigns_count: number;
  total_budget: number;
  has_activity: boolean;
}
export class ContactService {
  // Get all contacts with optional filtering
  async getContacts(filter: ContactsFilter = {}): Promise<ContactWithActivity[]> {
    let query = supabase
      .from('contacts')
      .select(`
        *,
        artists_count:artists(count)
      `)
      .is('deleted_at', null)
      .or('archived.is.null,archived.eq.false');

    // Apply search filter
    if (filter.search) {
      query = query.or(`name.ilike.%${filter.search}%,email.ilike.%${filter.search}%`);
    }

    // Apply type filter
    if (filter.contact_type && filter.contact_type !== 'all') {
      query = query.eq('client_type', filter.contact_type);
    }

    // Apply has campaigns filter
    if (filter.has_campaigns !== undefined) {
      if (filter.has_campaigns) {
        query = query.gt('campaigns_count', 0);
      } else {
        query = query.eq('campaigns_count', 0);
      }
    }

    // Apply recently added filter
    if (filter.recent_days) {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - filter.recent_days);
      query = query.gte('created_at', cutoffDate.toISOString());
    }
    // Apply sorting
    const sortBy = filter.sortBy || 'created_at';
    const sortOrder = filter.sortOrder || 'desc';
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

  const [error, result] = await noderr(query);
    
  if (error) {
    throw new DatabaseError(`Failed to fetch contacts: ${error.message}`);
  }
    
  const contacts = ((result as any)?.data as any[]) || [];
  
  return contacts.map((contact: any) => ({
      ...contact,
      contact_type: contact.client_type, // Map client_type to contact_type for frontend
      artists_count: contact.artists_count?.[0]?.count || 0,
      campaigns_count: 0,
      total_budget: 0,
      has_activity: (contact.artists_count?.[0]?.count || 0) > 0 || 
                   false
    }));
  }

  // Get single contact by ID
  async getContactById(id: string): Promise<Contact | null> {
    const [error, result] = await noderr(
      supabase
        .from('contacts')
        .select('*')
        .eq('id', id)
        .is('deleted_at', null)
        .single()
    );
    
    if (error) {
      if ((error as any).code === 'PGRST116') return null; // Not found
      throw new DatabaseError(`Failed to fetch client: ${error.message}`);
    }
    
    const contact = (result as any)?.data || null;
    if (contact) {
      contact.contact_type = contact.client_type; // Map client_type to contact_type for frontend
    }
    return contact;
  }

  // Create new contact
  async createContact(contactData: CreateContactData): Promise<{ success: true; data: Contact } | { success: false; error: ContactServiceError }> {
    // Validate required fields
    if (!contactData.name?.trim()) {
      return {
        success: false,
        error: {
          code: ContactErrorCode.VALIDATION,
          message: 'Contact name is required',
          field: 'name'
        }
      };
    }

    // Validate email format if provided
    if (contactData.email && !this.isValidEmail(contactData.email)) {
      return {
        success: false,
        error: {
          code: ContactErrorCode.VALIDATION,
          message: 'Invalid email format',
          field: 'email'
        }
      };
    }

    const [error, result] = await noderr(
      supabase
        .from('contacts')
        .insert({
          name: contactData.name.trim(),
          client_type: contactData.contact_type || 'individual',
          company_name: contactData.company_name?.trim() || null,
          website: contactData.website?.trim() || null,
          email: contactData.email?.trim() || null,
          phone: contactData.phone?.trim() || null
        })
        .select()
        .single()
    );
    
    if (error) {
      if ((error as any).code === '23505') { // Unique constraint violation
        return {
          success: false,
          error: {
            code: ContactErrorCode.DUPLICATE_EMAIL,
            message: 'A contact with this email already exists',
            field: 'email'
          }
        };
      }
      return {
        success: false,
        error: {
          code: ContactErrorCode.RLS_DENIED,
          message: `Failed to create contact: ${error.message}`
        }
      };
    }
    
    return { success: true, data: (result as any)?.data as Contact };
  }

  // Update existing contact
  async updateContact(id: string, updates: UpdateContactData): Promise<{ success: true; data: Contact } | { success: false; error: ContactServiceError }> {
    // Validate email format if provided
    if (updates.email && !this.isValidEmail(updates.email)) {
      return {
        success: false,
        error: {
          code: ContactErrorCode.VALIDATION,
          message: 'Invalid email format',
          field: 'email'
        }
      };
    }

    // Clean up data
    const cleanUpdates: any = {};
    if (updates.name !== undefined) cleanUpdates.name = updates.name.trim();
    if (updates.contact_type !== undefined) cleanUpdates.client_type = updates.contact_type;
    if (updates.company_name !== undefined) cleanUpdates.company_name = updates.company_name?.trim() || null;
    if (updates.website !== undefined) cleanUpdates.website = updates.website?.trim() || null;
    if (updates.email !== undefined) cleanUpdates.email = updates.email?.trim() || null;
    if (updates.phone !== undefined) cleanUpdates.phone = updates.phone?.trim() || null;
    if (updates.notes !== undefined) cleanUpdates.notes = updates.notes?.trim() || '';
    if (updates.tags !== undefined) cleanUpdates.tags = updates.tags || [];

    const [error, result] = await noderr(
      supabase
        .from('contacts')
        .update(cleanUpdates)
        .eq('id', id)
        .is('deleted_at', null)
        .select()
        .single()
    );
    
    if (error) {
      if ((error as any).code === 'PGRST116') {
        return {
          success: false,
          error: {
            code: ContactErrorCode.NOT_FOUND,
            message: 'Contact not found'
          }
        };
      }
      if ((error as any).code === '23505') {
        return {
          success: false,
          error: {
            code: ContactErrorCode.DUPLICATE_EMAIL,
            message: 'A contact with this email already exists',
            field: 'email'
          }
        };
      }
      return {
        success: false,
        error: {
          code: ContactErrorCode.RLS_DENIED,
          message: `Failed to update contact: ${error.message}`
        }
      };
    }
    
    return { success: true, data: (result as any)?.data as Contact };
  }

  // Legacy alias for updateClient
  async updateClient(id: string, updates: UpdateContactData): Promise<{ success: true; data: Contact } | { success: false; error: ContactServiceError }> {
    return this.updateContact(id, updates);
  }

  // Archive contact (soft delete)
  async archiveContact(id: string): Promise<void> {
    const [error] = await noderr(
      supabase
        .from('contacts')
        .update({ 
          archived: true 
        })
        .eq('id', id)
        .is('deleted_at', null)
    );
    
    if (error) {
      throw new DatabaseError(`Failed to archive contact: ${error.message}`);
    }
  }

  // Delete contact (permanent - admin only)
  async deleteContact(id: string): Promise<void> {
    // Check if contact has associated artists
    const [artistError, artistResult] = await noderr(
      supabase
        .from('artists')
        .select('id')
        .eq('client_id', id)
        .limit(1)
    );

    if (!artistError && (artistResult as any)?.data && ((artistResult as any).data as any[]).length > 0) {
      throw new ValidationError('Cannot delete contact with associated artists. Delete artists first.');
    }

    // Permanently delete the contact from the database
    const [error] = await noderr(
      supabase
        .from('contacts')
        .delete()
        .eq('id', id)
    );
    
    if (error) {
      throw new DatabaseError(`Failed to delete contact: ${error.message}`);
    }
  }

  // Legacy alias for deleteClient
  async deleteClient(id: string): Promise<void> {
    return this.deleteContact(id);
  }

  // Batch archive contacts (soft delete)
  async batchArchiveContacts(ids: string[]): Promise<BatchDeleteResult> {
    const result: BatchDeleteResult = {
      success: [],
      failed: []
    };

    for (const id of ids) {
      try {
        await this.archiveContact(id);
        result.success.push(id);
      } catch (error) {
        result.failed.push({
          id,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }

    return result;
  }

  // Batch delete contacts (permanent - admin only)
  async batchDeleteContacts(ids: string[]): Promise<BatchDeleteResult> {
    const result: BatchDeleteResult = {
      success: [],
      failed: []
    };

    for (const id of ids) {
      try {
        await this.deleteContact(id);
        result.success.push(id);
      } catch (error) {
        result.failed.push({
          id,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }

    return result;
  }

  // Legacy alias for batchDeleteClients
  async batchDeleteClients(ids: string[]): Promise<BatchDeleteResult> {
    return this.batchDeleteContacts(ids);
  }

  // Export contacts to CSV
  async exportContacts(ids?: string[]): Promise<string> {
    let query = supabase
      .from('contacts')
      .select('*')
      .is('deleted_at', null);

    if (ids && ids.length > 0) {
      query = query.in('id', ids);
    }

  const [error, result] = await noderr(query);
    
  if (error) throw new DatabaseError(`Failed to export contacts: ${error.message}`);
    
  const clients = ((result as any)?.data as any[]) || [];
    
    // Generate CSV content
    const headers = ['Name', 'Type', 'Company', 'Email', 'Phone', 'Website', 'Created', 'Tags'];
  const rows = clients.map((client: any) => [
      client.name,
      client.client_type || 'individual',
      client.company_name || '',
      client.email || '',
      client.phone || '',
      client.website || '',
      new Date(client.created_at).toLocaleDateString(),
      (client.tags || []).join('; ')
    ]);
    
  return [headers, ...rows].map((row: any[]) => row.map((cell: any) => `"${cell}"`).join(',')).join('\n');
  }

  // Legacy alias for exportClients
  async exportClients(ids?: string[]): Promise<string> {
    return this.exportContacts(ids);
  }

  // Get contact statistics
  async getContactStats(id: string): Promise<ContactStats> {
  const [error, result] = await noderr(
      supabase
        .from('client_hierarchy')
        .select('*')
        .eq('client_id', id)
    );

    if (error) {
      console.warn('Failed to fetch contact stats, returning defaults:', error);
      return {
        artists_count: 0,
        releases_count: 0,
        campaigns_count: 0,
        total_budget: 0
      };
    }

  const data = ((result as any)?.data as any[]) || [];
  const uniqueArtists = new Set(data.map((d: any) => d.artist_id).filter(Boolean));
  const uniqueReleases = new Set(data.map((d: any) => d.release_id).filter(Boolean));
  const uniqueCampaigns = new Set(data.map((d: any) => d.campaign_id).filter(Boolean));
  const totalBudget = data.reduce((sum: number, d: any) => sum + (d.campaign_budget || 0), 0);

    return {
      artists_count: uniqueArtists.size,
      releases_count: uniqueReleases.size,
      campaigns_count: uniqueCampaigns.size,
      total_budget: totalBudget
    };
  }

  // Legacy alias for getClientStats
  async getClientStats(id: string): Promise<ContactStats> {
    return this.getContactStats(id);
  }

  // Utility methods
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Find other contacts by matching domain on website or email
  async findContactsByDomain(domain: string, opts?: { excludeId?: string; limit?: number }): Promise<Contact[]> {
    const host = (domain || '').toLowerCase();
    if (!host) return [];

    let query = supabase
      .from('contacts')
      .select('*')
      .is('deleted_at', null)
      .or(`website.ilike.%${host}%,email.ilike.%@${host}`)
      .order('updated_at', { ascending: false });

    if (opts?.excludeId) {
      query = query.neq('id', opts.excludeId);
    }
    if (opts?.limit) {
      query = query.limit(opts.limit);
    }

    const [error, result] = await noderr(query);
    if (error) {
      console.warn('findContactsByDomain failed:', error);
      return [];
    }
    const contacts = (result as any)?.data || [];
    return contacts.map((contact: any) => ({
      ...contact,
      contact_type: contact.client_type, // Map client_type to contact_type for frontend
    }));
  }

  // Legacy alias for findClientsByDomain
  async findClientsByDomain(domain: string, opts?: { excludeId?: string; limit?: number }): Promise<Contact[]> {
    return this.findContactsByDomain(domain, opts);
  }

  // Get archived contacts
  async getArchivedContacts(): Promise<ContactWithActivity[]> {
    let query = supabase
      .from('contacts')
      .select(`
        *,
        artists_count:artists(count)
      `)
      .is('deleted_at', null)
      .eq('archived', true);

    const [error, result] = await noderr(query);
    
    if (error) throw new DatabaseError(`Failed to fetch archived contacts: ${error.message}`);
    
    const contacts = ((result as any)?.data as any[]) || [];
    return contacts.map((contact: any) => ({
      ...contact,
      contact_type: contact.client_type, // Map client_type to contact_type for frontend
      artists_count: contact.artists_count?.[0]?.count || 0,
      campaigns_count: 0,
      total_budget: 0,
      has_activity: (contact.artists_count?.[0]?.count || 0) > 0 || false
    }));
  }

  // Restore archived contact
  async restoreContact(id: string): Promise<void> {
    const [error] = await noderr(
      supabase
        .from('contacts')
        .update({ 
          archived: false 
        })
        .eq('id', id)
        .eq('archived', true)
    );
    
    if (error) {
      throw new DatabaseError(`Failed to restore contact: ${error.message}`);
    }
  }

  // Get sub-contacts for a specific contact
  async getContactContacts(contactId: string): Promise<ContactContact[]> {
    const [error, result] = await noderr(
      supabase
        .from('client_contacts')
        .select('*')
        .eq('client_id', contactId)
        .order('created_at', { ascending: false })
    );
    
    if (error) {
      throw new DatabaseError(`Failed to fetch contact contacts: ${error.message}`);
    }
    
    return (result as any)?.data || [];
  }

  // Get contacts by domain (for finding related contacts)
  async getContactsByDomain(domain: string, options: { excludeClientId?: string; limit?: number } = {}): Promise<Contact[]> {
    const { excludeClientId, limit = 10 } = options;
    
    let query = supabase
      .from('contacts')
      .select('*')
      .is('deleted_at', null)
      .or('archived.is.null,archived.eq.false')
      .or(`email.ilike.%${domain},website.ilike.%${domain}`)
      .limit(limit);
    
    if (excludeClientId) {
      query = query.neq('id', excludeClientId);
    }
    
    const [error, result] = await noderr(query);
    
    if (error) {
      throw new DatabaseError(`Failed to fetch contacts by domain: ${error.message}`);
    }
    
    const contacts = ((result as any)?.data as any[]) || [];
    return contacts.map((contact: any) => ({
      ...contact,
      contact_type: contact.client_type, // Map client_type to contact_type for frontend
    }));
  }

}

// Export singleton instance
export const contactService = new ContactService();
export const clientService = contactService; // Legacy alias